import { chunk } from 'lodash'
import { Metric } from 'components/visualization/MediaCard/mediaCardContext'
import MetricSereis, { MetricSeriesStrings } from 'enums/MetricSeries'
import { MetricSeries } from 'schemas/MetricSeriesSchema'
import { MediaPostMetric } from 'schemas/MediaPostMetricSchema'
import StreamSymbol from 'enums/StreamSymbol'
import { MediaSeriesWithMetricIndex } from '../../types'

const getSeriesNameToValue = (dataset: MetricSeries[]) =>
  dataset.reduce((accumulator: Record<MetricSeriesStrings, number | undefined>, data) => {
    const { seriesName, set } = data
    if (seriesName) {
      accumulator[seriesName] = set?.[0]
    }
    return accumulator
  }, {})

export const convertMetricToMediaCardData = (mediaMetric: MediaPostMetric) => {
  const { dataset, dataStreams } = mediaMetric
  const seriesNameToValue = getSeriesNameToValue(dataset || [])
  return {
    domain: mediaMetric.domain,
    dataStream: dataStreams?.[0],
    footer: new Map<Metric, number | undefined>([
      ['retweets', seriesNameToValue[MetricSereis.TOTAL_REPOSTS]],
      ['replies', seriesNameToValue[MetricSereis.TOTAL_REPLIES]],
      ['shares', seriesNameToValue[MetricSereis.TOTAL_REPOSTS]],
      ['comments', seriesNameToValue[MetricSereis.TOTAL_REPLIES]],
      ['likes', seriesNameToValue[MetricSereis.LIKES]],
      ['reblogs', seriesNameToValue[MetricSereis.TOTAL_REPOSTS]],
      ['views', seriesNameToValue[MetricSereis.VIEWS]]
    ])
  }
}

const getMediasWithMetricIndex = (
  metric: MediaPostMetric,
  metricIndex: number
): MediaSeriesWithMetricIndex[] =>
  metric.mediaSet?.map((mediaSeries) => ({ ...mediaSeries, metricIndex })) || []

export const groupMediasByPost = (metrics: MediaPostMetric[]): MediaSeriesWithMetricIndex[][] =>
  metrics.map((metric, metricIndex) => getMediasWithMetricIndex(metric, metricIndex))

export const groupMediasByMedia = (metrics: MediaPostMetric[]): MediaSeriesWithMetricIndex[][] =>
  metrics.reduce(
    (accumulator, metric, metricIndex) =>
      accumulator.concat(chunk(getMediasWithMetricIndex(metric, metricIndex))),
    [] as MediaSeriesWithMetricIndex[][]
  )

const getEpisodeDescription = (mediaMetric: MediaPostMetric) => {
  const { sentences, sentenceMetaTags, dataStreams } = mediaMetric
  if (dataStreams?.[0] !== StreamSymbol.PODCAST) {
    return ''
  }
  const podcastTranscriptIndex = (sentenceMetaTags ?? []).findIndex((tag) =>
    tag?.includes('EPISODE TRANSCRIPT')
  )

  const hasTranscript = podcastTranscriptIndex >= 0

  const episodeDescriptionSentences = sentences?.slice(
    0,
    hasTranscript ? podcastTranscriptIndex : undefined
  )
  return episodeDescriptionSentences?.flat().join(' ')
}

const getPodcastTranscript = (mediaMetric: MediaPostMetric) => {
  const { sentences, sentenceMetaTags, dataStreams } = mediaMetric
  if (dataStreams?.[0] !== StreamSymbol.PODCAST) {
    return ''
  }
  const podcastTranscriptIndex = (sentenceMetaTags ?? []).findIndex((tag) =>
    tag?.includes('EPISODE TRANSCRIPT')
  )
  const podcastTranscriptSentences = sentences?.slice(podcastTranscriptIndex)
  return podcastTranscriptSentences?.flat().join(' ')
}

export const convertMetricToDetail = (mediaMetric: MediaPostMetric) => {
  const {
    postId,
    postDate,
    link,
    picture,
    authorName,
    authorHandle,
    authorUrl,
    authorVerified,
    domain,
    dataStreams,
    sentences,
    dataset,
    photoUserTags,
    mentionedUrls
  } = mediaMetric
  return {
    postId,
    timestamp: Number(postDate),
    postUrl: link,
    avatarUrl: picture,
    authorName,
    authorHandle,
    authorPageUrl: authorUrl,
    isVerifiedAccount: authorVerified,
    domain,
    dataStream: dataStreams?.[0],
    message: sentences?.flat().join(' '),
    podcastEpisodeDescription: getEpisodeDescription(mediaMetric),
    podcastTranscript: getPodcastTranscript(mediaMetric),
    metricSeriesData: getSeriesNameToValue(dataset || []),
    photoUserTags: photoUserTags as string[],
    mentionedUrls
  }
}
